import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Pencil, Users, Bell, Send, Calendar, Mail, CheckCircle2, AlertTriangle, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { SeriesFormFields } from './SeriesList';

const emptyRelease = { volume_label: '', title: '', release_date: '', extension_id: '', note: '' };

export default function SeriesAdmin() {
  const { id } = useParams();
  const [series, setSeries] = useState(null);
  const [extensions, setExtensions] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [release, setRelease] = useState(emptyRelease);
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/admin/series/${id}`).then(r => setSeries(r.data)).catch(() => toast.error("Série introuvable"));
  useEffect(() => {
    load();
    api.get('/admin/extensions').then(r => setExtensions(r.data.filter(e => e.product_type === 'bd_manga')));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

  const openEdit = () => { setForm({ name: series.name, author: series.author, publisher: series.publisher, series_type: series.series_type, description: series.description, image_url: series.image_url }); setEditOpen(true); };
  const saveEdit = async () => {
    try { await api.put(`/admin/series/${id}`, form); toast.success("Série mise à jour"); setEditOpen(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const validate = async () => {
    try { await api.put(`/admin/series/${id}`, { status: 'active' }); toast.success("Série validée et visible par vos clients"); load(); }
    catch { toast.error("Erreur"); }
  };

  const announce = async (e) => {
    e.preventDefault();
    if (!release.volume_label.trim() || !release.release_date) { toast.error("Tome et date de sortie sont requis"); return; }
    if (!window.confirm(`Envoyer l'annonce à ${series.follower_count} abonné(s) ?`)) return;
    setSending(true);
    try {
      const res = await api.post(`/admin/series/${id}/releases`, { ...release, extension_id: release.extension_id || null });
      toast.success(`Annonce envoyée à ${res.data.sent_count} abonné${res.data.sent_count > 1 ? 's' : ''}${res.data.failed.length ? ` (${res.data.failed.length} échec)` : ''}`);
      setRelease(emptyRelease);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setSending(false); }
  };

  if (!series) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-violet-600 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="admin-series-detail">
      <Link to="/admin/series" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-series"><ArrowLeft className="h-4 w-4" /> Toutes les séries</Link>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Badge className={`text-xs border ${series.series_type === 'bd' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-rose-100 text-rose-700 border-rose-200'}`}>{series.series_type === 'bd' ? 'BD' : 'Manga'}</Badge>
            {series.status === 'proposee' && <Badge className="text-xs border bg-amber-100 text-amber-800 border-amber-200" data-testid="detail-proposed-badge">Proposée par {series.proposed_by}</Badge>}
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="series-detail-name">{series.name}</h1>
          <p className="text-slate-500 mt-1">{[series.author, series.publisher].filter(Boolean).join(' · ') || 'Auteur et éditeur non renseignés'}</p>
        </div>
        <div className="flex gap-2">
          {series.status === 'proposee' && <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={validate} data-testid="validate-series-btn"><CheckCircle2 className="h-4 w-4 mr-2" /> Valider la série</Button>}
          <Button variant="outline" className="rounded-full" onClick={openEdit} data-testid="edit-series-btn"><Pencil className="h-4 w-4 mr-2" /> Modifier</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          <form onSubmit={announce} className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="announce-form">
            <div className="flex items-center gap-2 mb-1"><Bell className="h-5 w-5 text-rose-500" /><h2 className="text-base md:text-lg font-bold text-slate-900">Annoncer une nouvelle sortie</h2></div>
            <p className="text-sm text-slate-500 mb-5">Un email sera envoyé aux {series.follower_count} abonné{series.follower_count > 1 ? 's' : ''} de cette série.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-xs font-medium text-slate-500">Tome / numéro *</Label><Input value={release.volume_label} onChange={e => setRelease(r => ({ ...r, volume_label: e.target.value }))} placeholder="Tome 12" className="mt-1 rounded-lg" data-testid="release-volume-input" /></div>
              <div><Label className="text-xs font-medium text-slate-500">Date de sortie *</Label><Input type="date" value={release.release_date} onChange={e => setRelease(r => ({ ...r, release_date: e.target.value }))} className="mt-1 rounded-lg" data-testid="release-date-input" /></div>
            </div>
            <div className="mt-4"><Label className="text-xs font-medium text-slate-500">Titre du tome (optionnel)</Label><Input value={release.title} onChange={e => setRelease(r => ({ ...r, title: e.target.value }))} className="mt-1 rounded-lg" data-testid="release-title-input" /></div>
            <div className="mt-4"><Label className="text-xs font-medium text-slate-500">Lier à une précommande (optionnel)</Label>
              <Select value={release.extension_id || 'none'} onValueChange={v => setRelease(r => ({ ...r, extension_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="release-extension-select"><SelectValue placeholder="Aucune" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune précommande liée</SelectItem>
                  {extensions.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Le client recevra un bouton « Précommander » dans l'email.</p>
            </div>
            <div className="mt-4"><Label className="text-xs font-medium text-slate-500">Mot de la boutique (optionnel)</Label><Textarea value={release.note} onChange={e => setRelease(r => ({ ...r, note: e.target.value }))} rows={3} className="mt-1 rounded-lg" placeholder="Disponible en boutique dès le jour de sortie !" data-testid="release-note-input" /></div>
            <Button type="submit" disabled={sending || series.follower_count === 0} className="mt-5 rounded-full bg-rose-600 hover:bg-rose-700" data-testid="announce-submit-btn">
              <Send className="h-4 w-4 mr-2" /> {sending ? 'Envoi en cours...' : `Envoyer aux ${series.follower_count} abonné${series.follower_count > 1 ? 's' : ''}`}
            </Button>
            {series.follower_count === 0 && <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Aucun abonné pour l'instant</p>}
          </form>

          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="releases-history">
            <h2 className="text-base md:text-lg font-bold text-slate-900 mb-4">Sorties annoncées ({series.releases.length})</h2>
            {series.releases.length === 0 ? <p className="text-sm text-slate-400">Aucune sortie annoncée pour le moment</p> : (
              <div className="space-y-3">
                {series.releases.map(r => (
                  <div key={r.id} className="flex items-start justify-between gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100" data-testid={`release-${r.id}`}>
                    <div>
                      <p className="font-semibold text-slate-900">{r.volume_label}{r.title ? ` – ${r.title}` : ''}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><Calendar className="h-3.5 w-3.5" /> Sortie le {formatDate(r.release_date)}</p>
                      {r.extension_name && <p className="text-sm text-violet-600 flex items-center gap-1.5 mt-1"><Link2 className="h-3.5 w-3.5" /> {r.extension_name}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <Badge className="text-xs border bg-emerald-100 text-emerald-800 border-emerald-200"><Mail className="h-3 w-3 mr-1" />{r.sent_count}/{r.recipients_count} envoyés</Badge>
                      <p className="text-xs text-slate-400 mt-1">{new Date(r.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="followers-list">
            <div className="flex items-center gap-2 mb-4"><Users className="h-5 w-5 text-violet-600" /><h2 className="text-base md:text-lg font-bold text-slate-900">Abonnés ({series.followers.length})</h2></div>
            {series.followers.length === 0 ? <p className="text-sm text-slate-400">Personne ne suit encore cette série</p> : (
              <ul className="divide-y divide-slate-100">
                {series.followers.map(f => (
                  <li key={f.id} className="py-2.5 flex items-center justify-between text-sm" data-testid={`follower-${f.id}`}>
                    <span className="text-slate-800">{f.email}</span>
                    <span className="text-xs text-slate-400">{new Date(f.created_at).toLocaleDateString('fr-FR')}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-series-dialog">
          <DialogHeader><DialogTitle>Modifier la série</DialogTitle></DialogHeader>
          <SeriesFormFields form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-full">Annuler</Button>
            <Button onClick={saveEdit} className="rounded-full" data-testid="series-edit-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
